import React from 'react';

import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import type { ListDocumentsParams } from '@lumapps/medias-document/api';
import type { DocumentV1, MoveDocumentNavigationState } from '@lumapps/medias-document/types';
import { useSelector } from '@lumapps/redux/react';

import { MOVE_DOCUMENT_FOLDER_STATUS } from '../constants';
import { useFolderContents } from './useFolderContents';
import { useGetDocument } from './useGetDocument';
import { useGetDocumentPermissions } from './useGetDocumentPermissions';

export interface UseMoveDocumentNavigationParams
    extends Partial<Pick<ListDocumentsParams, 'searchTags' | 'searchTypes' | 'sortOrder'>> {
    /** The initial document that determines the starting folder of the dialog. */
    initialDocument: DocumentV1 | null;
    /** If `false`, the hook will not fetch any data. */
    isEnabled: boolean;
}

/**
 * Manages navigation for a "move document" dialog using a "fetch-on-navigate" approach.
 *
 * @param {UseMoveDocumentNavigationParams}
 */
export const useMoveDocumentNavigation = ({
    initialDocument,
    isEnabled,
    searchTags,
    searchTypes,
    sortOrder,
}: UseMoveDocumentNavigationParams) => {
    const currentSiteId = useSelector(instanceIdSelector);

    // The ID of the folder the user is currently viewing.
    const [currentFolderId, setCurrentFolderId] = React.useState<string | null>(initialDocument?.parentFolder ?? null);

    const currentFolderDocPath = React.useMemo(
        () =>
            currentSiteId && currentFolderId
                ? `provider=local/site=${currentSiteId}/resource=${currentFolderId}`
                : undefined,
        [currentFolderId, currentSiteId],
    );

    // Fetch the full details of the current folder to get its name and parent ID.
    const { data: currentFolderData, isLoading: isFolderDataLoading } = useGetDocument(
        { docPath: currentFolderDocPath! },
        { enabled: isEnabled && Boolean(currentFolderDocPath) },
    );

    // Fetch the contents (files and sub-folders) of the current folder.
    const folderContents = useFolderContents({
        folderId: currentFolderId,
        isEnabled,
        searchTags,
        searchTypes,
        sortOrder,
    });

    // Fetch the permissions to write on the document
    const { data: documentPermissions } = useGetDocumentPermissions(
        {
            docPath: folderContents.currentPath,
            searchParameters: { shared: true, trashed: false, recursive: false },
        },
        { enabled: Boolean(folderContents.currentPath) },
    );

    /**
     * Navigates into a specified folder.
     *
     * @param {DocumentV1} item The folder to navigate into.
     */
    const navigateToFolder = React.useCallback((item: DocumentV1) => {
        if (item.isFolder) {
            setCurrentFolderId(item.id);
        }
    }, []);

    /**
     * Navigates to the parent of the currently viewed folder.
     */
    const navigateToParent = React.useCallback(() => {
        setCurrentFolderId(currentFolderData?.parentFolder ?? null);
    }, [currentFolderData]);

    const folderState: MoveDocumentNavigationState = React.useMemo(() => {
        if (currentFolderId === null) {
            return { status: MOVE_DOCUMENT_FOLDER_STATUS.ROOT };
        }

        if (currentFolderData) {
            return { status: MOVE_DOCUMENT_FOLDER_STATUS.LOADED, folder: currentFolderData };
        }

        return { status: MOVE_DOCUMENT_FOLDER_STATUS.LOADING, folderId: currentFolderId };
    }, [currentFolderId, currentFolderData]);

    return {
        ...folderContents,
        folderState,
        isLoading: folderContents.isLoading || (isFolderDataLoading && Boolean(currentFolderDocPath)),
        isInitialFolder: currentFolderId === (initialDocument?.parentFolder ?? null),
        navigateToParent,
        navigateToFolder,
        documentPermissions,
    };
};
